/**
 * The read-out as the prospect sees it, without leaving the admin.
 *
 * Renders the PUBLIC ResultView against the PUBLIC payload, fetched from a
 * builder shared with the magic-link endpoint. Anything less faithful — a
 * summary, a re-implementation, a table of the same fields — stops being a
 * preview the first time the two drift, and the read-out is the product.
 *
 * The CTA and the explainer videos are inert here: an admin checking a
 * read-out should not be counted as a prospect clicking through, and the view
 * count is a conversion metric.
 */
import React from 'react';
import { Modal, Spin, Empty } from 'antd';
import { FormattedMessage } from 'react-intl';
import { ResultView } from '../../../pages/PublicHypothesis/ResultView';
import { useGetHypothesisResultPreviewQuery } from '../../../services/nestjs-base-api';

interface Props {
    jwt: string;
    /** Null closes the modal. */
    subjectId: number | null;
    onClose: () => void;
}

export const ResultPreviewModal: React.FC<Props> = ({ jwt, subjectId, onClose }) => {
    const { data, isFetching, isError } = useGetHypothesisResultPreviewQuery(
        { jwtToken: jwt, subjectId: subjectId as number },
        { skip: !subjectId },
    );

    return (
        <Modal
            open={!!subjectId}
            onCancel={onClose}
            footer={null}
            width={860}
            title={
                <FormattedMessage
                    id="publicHypothesis.admin.previewTitle"
                    defaultMessage="The read-out, as they see it"
                />
            }
            styles={{ body: { maxHeight: '75vh', overflowY: 'auto', background: '#fbfbfd' } }}
        >
            {isFetching ? (
                <div style={{ padding: 48, textAlign: 'center' }}>
                    <Spin />
                </div>
            ) : isError || !data || data.state !== 'ready' ? (
                <Empty
                    description={
                        <FormattedMessage
                            id="publicHypothesis.admin.previewUnavailable"
                            defaultMessage="No finished read-out for this one yet."
                        />
                    }
                />
            ) : (
                <ResultView
                    result={data}
                    // Points at the same place the real page does, but nothing
                    // here reports a click: this is not a prospect converting.
                    signUpUrl="/sign-up"
                />
            )}
        </Modal>
    );
};
